import * as React from 'react';

import {
  Badge,
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  FloatingToolbar,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucidePause, LucidePlay, LucideTrash2, LucideX } from 'lucide-react';

import * as state from '../../_state/automationList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { AutomationRecord } from '../../types';

interface AutomationBulkToolbarProps {
  selectedRows: AutomationRecord[];
  onClearSelection: () => void;
}

export const AutomationBulkToolbar: React.FC<AutomationBulkToolbarProps> = ({
  selectedRows,
  onClearSelection,
}) => {
  const t = useTranslations();
  const toast = useToast();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const bulkActivate = useSetAtom(state.$bulkActivateAutomation);
  const bulkPause = useSetAtom(state.$bulkPauseAutomation);
  const bulkDelete = useSetAtom(state.$bulkDeleteAutomation);

  const selectedIds = React.useMemo(
    () => selectedRows.map((row) => row.id),
    [selectedRows],
  );

  const handleBulkActivate = React.useCallback(async () => {
    setIsProcessing(true);
    try {
      const result = await bulkActivate({ ids: selectedIds });
      const { successCount, failedCount } =
        result.automationsAutomationBulkActivate;
      if (failedCount > 0) {
        toast.warning(
          t.bulk.messages.activatePartial
            .replace('{success}', String(successCount))
            .replace('{failed}', String(failedCount)),
        );
      } else {
        toast.success(
          t.bulk.messages.activateSuccess.replace(
            '{count}',
            String(successCount),
          ),
        );
      }
      onClearSelection();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.bulk.messages.activateError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [bulkActivate, selectedIds, toast, t, onClearSelection]);

  const handleBulkPause = React.useCallback(async () => {
    setIsProcessing(true);
    try {
      const result = await bulkPause({ ids: selectedIds });
      const { successCount, failedCount } =
        result.automationsAutomationBulkPause;
      if (failedCount > 0) {
        toast.warning(
          t.bulk.messages.pausePartial
            .replace('{success}', String(successCount))
            .replace('{failed}', String(failedCount)),
        );
      } else {
        toast.success(
          t.bulk.messages.pauseSuccess.replace('{count}', String(successCount)),
        );
      }
      onClearSelection();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.bulk.messages.pauseError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [bulkPause, selectedIds, toast, t, onClearSelection]);

  const handleBulkDelete = React.useCallback(async () => {
    setIsProcessing(true);
    try {
      const result = await bulkDelete({ ids: selectedIds });
      const { successCount, failedCount } =
        result.automationsAutomationBulkDelete;
      if (failedCount > 0) {
        toast.warning(
          t.bulk.messages.deletePartial
            .replace('{success}', String(successCount))
            .replace('{failed}', String(failedCount)),
        );
      } else {
        toast.success(
          t.bulk.messages.deleteSuccess.replace(
            '{count}',
            String(successCount),
          ),
        );
      }
      onClearSelection();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.bulk.messages.deleteError,
      );
    } finally {
      setIsProcessing(false);
      setIsDeleteDialogOpen(false);
    }
  }, [bulkDelete, selectedIds, toast, t, onClearSelection]);

  return (
    <>
      <FloatingToolbar
        visible={selectedRows.length > 0}
        className="@AutomationBulkToolbar"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>
            {selectedRows.length === 1
              ? t.bulk.selected
              : t.bulk.selectedPlural}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              icon={<LucidePlay className="size-4" />}
              isDisabled={isProcessing}
              onPress={handleBulkActivate}
            >
              {t.bulk.actions.activate}
            </Button>
            <Button
              variant="outline"
              icon={<LucidePause className="size-4" />}
              isDisabled={isProcessing}
              onPress={handleBulkPause}
            >
              {t.bulk.actions.pause}
            </Button>
            <Button
              variant="outline"
              color="failure"
              icon={<LucideTrash2 className="size-4" />}
              isDisabled={isProcessing}
              onPress={() => setIsDeleteDialogOpen(true)}
            >
              {t.bulk.actions.delete}
            </Button>
          </div>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={onClearSelection}
          />
        </div>
      </FloatingToolbar>

      <DialogOverlay
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <DialogContent position="center" className="w-full min-w-[32rem]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              <LucideTrash2 className="size-5 text-failure" />
              {t.bulk.deleteDialog.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 p-4">
            <p>
              {t.bulk.deleteDialog.message.replace(
                '{count}',
                String(selectedRows.length),
              )}
            </p>
            <p className="text-sm text-neutral/60">
              {t.bulk.deleteDialog.warning}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsDeleteDialogOpen(false)}
              isDisabled={isProcessing}
            >
              {t.bulk.deleteDialog.cancel}
            </Button>
            <Button
              variant="destructive"
              onPress={handleBulkDelete}
              isDisabled={isProcessing}
              isLoading={isProcessing}
            >
              {t.bulk.deleteDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogOverlay>
    </>
  );
};
